import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Alert, Collapse } from 'reactstrap';
import { head, isNil } from 'lodash';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import Switch from '../../common/Switch';
import { modelOf } from '../../../prop-types';
import AdditionalService from '../../../models/AdditionalService';
import WysiwygContent from '../../common/WysiwygContent';
import Price from '../Price';
import FormGroupField from '../../form/FormGroupField';
import CheckboxField from '../../form/CheckboxField';
import RadioField from '../../form/RadioField';
import ServiceOptionType from '../../../types/ServiceOptionType';
import FormField from '../../../models/FormField';
import UIStore from '../../../store/UIStore';
import SelectField from '../../form/SelectField';
import { formatPrice } from '../../../util/number';
import CurrencyStore from '../../../store/CurrencyStore';
import ProductImageModel from '../../../models/ProductImage';
import ImageLightbox from '../../common/ImageLightbox';
import Product from '../../../models/Product';
import Icon from '../../common/Icon';

const InputStatus = {
  NONE: 'NONE',
  TEXT_TOO_LONG: 'TEXT_TOO_LONG',
  TEXT_REQUIRED: 'TEXT_REQUIRED'
};

const DEFAULT_SERVICE_OPTIONS_CONTAINER_HEIGHT = 150;
const DEFAULT_SERVICE_OPTIONS_COUNT_BREAK = 10;
const DEFAULT_SERVICE_OPTIONS_ADD_HEIGHT = 15;

const labels = {
  postalCodeRequired: (
    <FormattedMessage
      id="additionalService.postalCodeRequired"
      defaultMessage="Eligible postal code is required for the service"
    />
  ),
  postalCodeIsInvalid: (
    <FormattedMessage
      id="additionalService.postalCodeIsInvalid"
      defaultMessage="Postal code is invalid for this service"
    />
  )
};

function createImage(index, product_id, path, alt) {
  return {
    index: index,
    product_id: product_id,
    path: path,
    alt: alt
  };
}

@observer
export class ProductAdditionalService extends Component {
  // This components logic should perhaps be remade to use eg.
  // reactstrap components.
  state = {
    inputStatus: InputStatus.NONE,
    selectedDropdown: null,
    lightboxIsOpen: false,
    activeImage: 0
  };

  toggleLightbox = (product_id = '') => {
    this.setState({
      lightboxIsOpen: !this.state.lightboxIsOpen,
      activeImage: this.getImageIndex(product_id)
    });
  };

  radio = (fieldName, props) => {
    const { formName, radioField } = this.props;

    if (!radioField) {
      return null;
    }

    return (
      radioField.active && (
        <RadioField
          className={'ProductAdditionalService__RadioFieldSet'}
          inline={false}
          field={radioField}
          formName={formName}
          fieldName={fieldName}
          handleChange={this.handleRadioChange}
          {...props}
        />
      )
    );
  };

  checkbox = (fieldName, props) => {
    const { formName, checkboxField } = this.props;

    if (!checkboxField) {
      return null;
    }

    return (
      checkboxField.active && (
        <CheckboxField
          className={'ProductAdditionalService__CheckboxFieldSet'}
          key={fieldName}
          field={checkboxField}
          formName={formName}
          fieldName={fieldName}
          handleChange={this.handleCheckboxChange}
          {...props}
        />
      )
    );
  };

  dropdown = (fieldName, props) => {
    const { formName, dropdownField } = this.props;

    if (!dropdownField) {
      return null;
    }

    return (
      dropdownField.active && (
        <SelectField
          key={fieldName}
          className={'ProductAdditionalService__DropdownFieldSet'}
          field={dropdownField}
          formName={formName}
          fieldName={fieldName}
          handleChange={this.handleDropdownChange}
          style={{
            width: '100%',
            color: '#333'
          }}
          {...props}
        />
      )
    );
  };

  textarea = (fieldName, props) => {
    const { formName, text, textField } = this.props;

    if (!textField) {
      return null;
    }

    return (
      textField.active && (
        <FormGroupField
          field={textField}
          formName={formName}
          fieldName={fieldName}
          type="textarea"
          value={text}
          {...props}
        />
      )
    );
  };

  createService = (
    id,
    productId,
    isSelected,
    text,
    options,
    textMaxLength,
    textRequired
  ) => {
    return {
      id,
      productId,
      isSelected,
      text,
      options,
      textMaxLength,
      textRequired
    };
  };

  handleServiceToggle = () => {
    const {
      service,
      isSelected,
      text,
      selectedOptions,
      handleSelectedServices
    } = this.props;

    handleSelectedServices(
      this.createService(
        service.id,
        service.product_id,
        !isSelected,
        text,
        selectedOptions,
        service.text_max_length,
        service.text_required
      )
    );
  };

  handleTextChange = (e) => {
    const {
      service,
      isSelected,
      selectedOptions,
      handleSelectedServices
    } = this.props;

    if (service.text_required && e.target.value.length === 0) {
      this.setState({
        inputStatus: InputStatus.TEXT_REQUIRED
      });
    } else if (
      service.text_max_length &&
      e.target.value.length > service.text_max_length
    ) {
      this.setState({
        inputStatus: InputStatus.TEXT_TOO_LONG
      });
    } else {
      if (this.state.inputStatus !== InputStatus.NONE) {
        this.setState({
          inputStatus: InputStatus.NONE
        });
      }
    }

    handleSelectedServices(
      this.createService(
        service.id,
        service.product_id,
        isSelected,
        e.target.value,
        selectedOptions,
        service.text_max_length,
        service.text_required
      )
    );
  };

  getCheckboxValues = (value) => {
    const { selectedOptions } = this.props;
    let currentOptions = [];
    if (selectedOptions && selectedOptions.checkbox) {
      currentOptions = selectedOptions.checkbox.slice();
    }

    const hasValue = currentOptions.indexOf(value);
    if (hasValue !== -1) {
      currentOptions.splice(hasValue, 1);
    } else {
      currentOptions.push(value);
    }

    return currentOptions;
  };

  handleCheckboxChange = (e) => {
    const {
      text,
      service,
      isSelected,
      checkboxField,
      selectedOptions,
      handleSelectedServices
    } = this.props;
    let newOptions = {};
    const values = this.getCheckboxValues(e.target.value);

    if (selectedOptions) {
      newOptions = selectedOptions;
    }
    newOptions.checkbox = values;

    checkboxField.setValue(values);
    handleSelectedServices(
      this.createService(
        service.id,
        service.product_id,
        isSelected,
        text,
        newOptions,
        service.text_max_length,
        service.text_required
      )
    );
  };

  handleRadioChange = (e) => {
    const {
      text,
      service,
      isSelected,
      radioField,
      selectedOptions,
      handleSelectedServices
    } = this.props;
    let newOptions = {};

    if (selectedOptions) {
      newOptions = selectedOptions;
    }
    newOptions.radio = e.target.value;

    this.setState({
      activeImage: this.getImageIndex(e.target.value)
    });
    radioField.setValue(e.target.value);
    handleSelectedServices(
      this.createService(
        service.id,
        service.product_id,
        isSelected,
        text,
        newOptions,
        service.text_max_length,
        service.text_required
      )
    );
  };

  handleDropdownChange = (e) => {
    const {
      text,
      service,
      isSelected,
      dropdownField,
      selectedOptions,
      handleSelectedServices
    } = this.props;
    let newOptions = {};

    if (selectedOptions) {
      newOptions = selectedOptions;
    }
    newOptions.dropdown = e.target.value;

    this.setState({
      selectedDropdown: e.target.value,
      activeImage: this.getImageIndex(e.target.value)
    });

    dropdownField.setValue(e.target.value);
    handleSelectedServices(
      this.createService(
        service.id,
        service.product_id,
        isSelected,
        text,
        newOptions,
        service.text_max_length,
        service.text_required
      )
    );
  };

  renderHeaderHtml = () => {
    const { service } = this.props;

    return service.header_html.length > 0 ? (
      <WysiwygContent html={service.header_html} />
    ) : null;
  };

  renderServiceSelectionText = () => {
    const { service } = this.props;

    return (
      <div className="ProductAdditionalService__toggle-description">
        {service.selection_text}
        {service.is_required && (
          <span className="ProductAdditionalService__required-service">*</span>
        )}
      </div>
    );
  };

  renderServiceToggler = () => {
    const { isSelected, service, product, postalCodeField } = this.props;
    const isRequired = service.is_required;
    const postalCodeIsRequired = service.requires_area_validation;
    const ifValidatedService =
      postalCodeIsRequired &&
      product.getValidatedService({
        productId: product.id,
        postalCode: postalCodeField.value,
        serviceId: service.id
      });

    return (
      <div className="ProductAdditionalService__toggle">
        <div className="ProductAdditionalService__toggle-switch">
          <Switch
            checked={isSelected}
            disabled={
              isRequired ||
              (postalCodeIsRequired && (isRequired || !ifValidatedService))
            }
            onChange={this.handleServiceToggle}
          >
            {this.renderServiceSelectionText()}
          </Switch>
        </div>
        {
          <div className="ProductAdditionalService__toggle-price">
            {this.renderPrice(service.price)}
          </div>
        }
      </div>
    );
  };

  renderDescription = () => {
    const { service } = this.props;

    return service.description.length > 0 ? (
      <WysiwygContent html={service.description} />
    ) : null;
  };

  renderPrice = (price) => {
    const { withTax } = this.props;

    if (price.with_tax > 0) {
      return <Price price={price.getPrice(withTax)} />;
    }
    return null;
  };

  renderTextInput = () => {
    const { service } = this.props;

    if (!service.enable_text_input) {
      return null;
    }

    return (
      <div className="ProductAdditionalService__content-text">
        {service.text_input_info && (
          <div className="ProductAdditionalService__text-description">
            {service.text_input_info}
          </div>
        )}
        {this.textarea('text_' + service.id, {
          label: service.text_max_length ? (
            <FormattedMessage
              id="additionalService.typeTextHereLengthSentence"
              defaultMessage="Type your text here. Maximum of {maxLength} characters."
              values={{
                maxLength: service.text_max_length
              }}
            />
          ) : (
            <FormattedMessage
              id="additionalService.typeTextHereSentence"
              defaultMessage="Type your text here."
            />
          ),
          onChange: this.handleTextChange
        })}
      </div>
    );
  };

  getOptionLabel = (option) => {
    const { withTax } = this.props;

    return (
      <div className="ProductAdditionalService__option-label">
        <div className="ProductAdditionalService__option-label-text">
          {option.selection_text}
        </div>
        {option.price.with_tax > 0 && (
          <div className="ProductAdditionalService__option-label-price">
            <Price price={option.price.getPrice(withTax)} />
          </div>
        )}
        {this.hasImages() && (
          <div
            className="ProductAdditionalService__option-label-image"
            onClick={() => this.toggleLightbox(option.product_id)}
          >
            {option.image && (
              <img
                src={option.image}
                alt={option.selection_text}
                loading="lazy"
              />
            )}
          </div>
        )}
      </div>
    );
  };

  getSelectOptionLabel = (option) => {
    const { withTax, currencyStore } = this.props;
    const price = option.price.getPrice(withTax);

    return (
      <Fragment>
        {option.selection_text}
        {price > 0
          ? '  ' + formatPrice(price, currencyStore.activeCurrency)
          : null}
      </Fragment>
    );
  };

  getDropdownOptionImage = () => {
    const { service } = this.props;
    const { selectedDropdown } = this.state;
    let filteredOption;

    if (selectedDropdown === null) {
      filteredOption = head(
        service.options.filter(
          (option) => option.type === ServiceOptionType.DROPDOWN
        )
      );
    } else {
      filteredOption = head(
        service.options.filter((option) => {
          return (
            option.type === ServiceOptionType.DROPDOWN &&
            option.product_id === selectedDropdown.toString()
          );
        })
      );
    }

    return filteredOption &&
      filteredOption.image &&
      filteredOption.image.length > 0
      ? createImage(
        0,
        filteredOption.product_id,
        filteredOption.image,
        filteredOption.selection_text
      )
      : null;
  };

  hasContent = () => {
    const { service } = this.props;
    return !!(
      service.description ||
      service.enable_text_input ||
      service.options.length > 0
    );
  };

  hasImages = () => {
    const { service } = this.props;

    if (service.options.length === 0) {
      return false;
    }

    return service.options.filter((option) => !!option.image).length > 0;
  };

  getImageIndex = (product_id) => {
    const images = this.getImages();
    let value = 0;

    images.forEach((image, index) => {
      if (image.product_id.toString() === product_id.toString()) {
        value = index;
      }
    });
    return value;
  };

  getImages = () => {
    const { service } = this.props;

    if (service.options.length === 0) {
      return null;
    }

    return service.options
      .filter((option) => {
        return !!(option.image && option.image.length > 0);
      })
      .map((option, index) => {
        return createImage(
          index,
          option.product_id,
          option.image,
          option.selection_text
        );
      });
  };

  renderSelectedDropdown = () => {
    const { service, withTax } = this.props;
    const { selectedDropdown } = this.state;
    if (!selectedDropdown) {
      return null;
    }

    const filteredOption = head(
      service.options.filter((option) => {
        return (
          option.type === ServiceOptionType.DROPDOWN &&
          option.product_id === selectedDropdown.toString()
        );
      })
    );

    if (!filteredOption) {
      return null;
    }

    const price = filteredOption.price.getPrice(withTax);
    if (price <= 0) {
      return null;
    }

    return (
      <div className="ProductAdditionalService__dropdown-info">
        <FormattedMessage
          id="additionalService.optionPriceSentence"
          defaultMessage="The price for this option is {price}"
          values={{
            price: (
              <b>
                <Price price={price} />
              </b>
            )
          }}
        />
      </div>
    );
  };

  renderDropdownImage = () => {
    const image = this.getDropdownOptionImage();
    if (!image) {
      return null;
    }

    return (
      <div
        className="ProductAdditionalService__dropdown-image"
        onClick={() => this.toggleLightbox(image.product_id)}
      >
        <img src={image.path} alt={image.alt} loading="lazy" />
      </div>
    );
  };

  renderServiceOptions = () => {
    const {
      service,
      radioField,
      checkboxField,
      dropdownField,
      uiStore
    } = this.props;

    if (service.options.length === 0) {
      return null;
    }

    const radio = radioField ? (
      <div className="ProductAdditionalService__radio-option">
        {!uiStore.isSmallest && (
          <style>
            {this.getCSS(
              service.options,
              '.ProductAdditionalService__radio-option-field'
            )}
          </style>
        )}
        {this.radio(`radioOptions_${service.id}`, {
          options: service.options
            .filter((option) => option.type === ServiceOptionType.RADIO)
            .map((option) => ({
              value: String(option.product_id),
              label: this.getOptionLabel(option)
            }))
        })}
      </div>
    ) : null;

    const checkbox = checkboxField ? (
      <div className="ProductAdditionalService__checkbox-option">
        {!uiStore.isSmallest && (
          <style>
            {this.getCSS(
              service.options,
              '.ProductAdditionalService__checkbox-option-field'
            )}
          </style>
        )}
        {this.checkbox(`checkboxOptions_${service.id}`, {
          options: service.options
            .filter((option) => option.type === ServiceOptionType.CHECKBOX)
            .map((option) => ({
              value: String(option.product_id),
              label: this.getOptionLabel(option)
            }))
        })}
      </div>
    ) : null;

    const dropdown = dropdownField ? (
      <div className="ProductAdditionalService__dropdown">
        {!uiStore.isSmallest && (
          <style>
            {this.getCSS(
              service.options,
              '.ProductAdditionalService__dropdown-option-field'
            )}
          </style>
        )}
        {this.dropdown(`dropdownOptions_${service.id}`, {
          options: service.options
            .filter((option) => option.type === ServiceOptionType.DROPDOWN)
            .map((option) => ({
              value: String(option.product_id),
              label: this.getSelectOptionLabel(option)
            }))
        })}
      </div>
    ) : null;

    return (
      <Fragment>
        {radio}
        {checkbox}
        {dropdown}
        {this.renderSelectedDropdown()}
        {this.renderDropdownImage()}
      </Fragment>
    );
  };

  getCSS = (services, className) => {
    // Need to somehow calculate height dynamically to divide fields into two columns.
    let height = DEFAULT_SERVICE_OPTIONS_CONTAINER_HEIGHT;
    if (services.length > DEFAULT_SERVICE_OPTIONS_COUNT_BREAK) {
      const addedHeight = services.length - DEFAULT_SERVICE_OPTIONS_COUNT_BREAK;
      height =
        DEFAULT_SERVICE_OPTIONS_CONTAINER_HEIGHT +
        addedHeight * DEFAULT_SERVICE_OPTIONS_ADD_HEIGHT;
    }

    return `${className}
     {
      flex-flow: column wrap !important;
      align-content: start;
      height: ${height}px
    }`;
  };

  showToggle = () => {
    const { service } = this.props;
    const onlyDropdown = !!!service.options.filter(
      (option) => option.type !== ServiceOptionType.DROPDOWN
    ).length;

    return !(
      service.is_required &&
      service.price.with_tax === 0 &&
      onlyDropdown
    );
  };

  renderError = () => {
    const { inputStatus } = this.state;
    let message = null;

    switch (inputStatus) {
      case InputStatus.TEXT_REQUIRED:
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="validation.additionalService.TEXT_REQUIRED"
              defaultMessage="Additional service requires text input."
            />
          </Alert>
        );
        break;
      case InputStatus.TEXT_TOO_LONG:
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="validation.additionalService.TEXT_TOO_LONG"
              defaultMessage="Too many characters."
            />
          </Alert>
        );
        break;
      case InputStatus.NONE:
      default:
        break;
    }
    return message;
  };

  renderLightbox = () => {
    const { service } = this.props;
    const images = this.getImages();

    if (
      !service.product_id ||
      !images ||
      images.length === 0 ||
      !this.state.lightboxIsOpen
    ) {
      return null;
    }

    const lightboxImages = images.map((image, index) => {
      return ProductImageModel.create({
        id: index,
        product_id: service.product_id,
        sizes: {
          full: image.path
        },
        description: ''
      });
    });

    return (
      <ImageLightbox
        images={lightboxImages}
        mainImageIndex={this.state.activeImage}
        onClick={this.toggleLightbox}
      />
    );
  };

  postalCodeValidationState = () => {
    const { product, service, postalCodeField } = this.props;
    const postalCodeIsRequired = service.requires_area_validation;

    return (
      postalCodeIsRequired &&
      product.getValidatedService({
        productId: product.id,
        postalCode: postalCodeField.value,
        serviceId: service.id
      })
    );
  };

  renderPostalCodeRequiredText = () => {
    const validPostalCode = this.postalCodeValidationState();

    return (
      <div
        className={classNames('ProductAdditionalService__information-text', {
          'ProductAdditionalService__information-text--invalid':
            validPostalCode === false
        })}
      >
        <Icon
          name={
            validPostalCode === false ? 'exclamation-triangle' : 'info-circle'
          }
        />
        {validPostalCode === false
          ? labels.postalCodeIsInvalid
          : labels.postalCodeRequired}
      </div>
    );
  };

  render() {
    const { service, isSelected } = this.props;
    const postalCodeIsRequired = service.requires_area_validation;

    if (!service) {
      return null;
    }

    const validPostalCode = this.postalCodeValidationState();

    return (
      <div className="ProductAdditionalService">
        {this.renderHeaderHtml()}
        {this.showToggle()
          ? this.renderServiceToggler()
          : this.renderServiceSelectionText()}
        {this.hasContent() && (
          <Collapse isOpen={isSelected}>
            <div className="ProductAdditionalService__content">
              {this.renderDescription()}
              {this.renderTextInput()}
              {this.renderServiceOptions()}
              {this.renderError()}
              {this.renderLightbox()}
            </div>
          </Collapse>
        )}
        {postalCodeIsRequired && !validPostalCode && this.renderPostalCodeRequiredText()}
      </div>
    );
  }
}

ProductAdditionalService.propTypes = {
  currencyStore: modelOf(CurrencyStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  product: modelOf(Product).isRequired,
  service: modelOf(AdditionalService).isRequired,
  formName: PropTypes.string.isRequired,
  handleSelectedServices: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
  withTax: PropTypes.bool.isRequired,
  checkboxField: modelOf(FormField),
  dropdownField: modelOf(FormField),
  radioField: modelOf(FormField),
  textField: modelOf(FormField),
  postalCodeField: modelOf(FormField),
  selectedOptions: PropTypes.object
};

export default inject((stores, props) => ({
  currencyStore: stores.currencyStore,
  uiStore: stores.uiStore,
  withTax: isNil(props.withTax)
    ? stores.accountStore.showPricesWithTax
    : props.withTax
}))(ProductAdditionalService);



// WEBPACK FOOTER //
// ./src/components/product/ProductAdditionalService/index.jsx